import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { readApiEnvelopeError, readHttpApiErrorMessage, unwrapApiData } from '../http/api-response.util';
import { Translation } from '../i18n/translation.service';

/** Normalised failure for every admin API call: envelope `success:false` or an HTTP error. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    override readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

/** Builds HttpParams, dropping null/undefined/empty-string values. */
export function toHttpParams(params?: QueryParams): HttpParams {
  let result = new HttpParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    result = result.set(key, String(value));
  }
  return result;
}

@Injectable({ providedIn: 'root' })
export class ApiClient {
  private readonly http = inject(HttpClient);
  private readonly translation = inject(Translation);

  get<T>(url: string, params?: QueryParams): Observable<T> {
    return this.handle<T>(this.http.get<unknown>(url, { params: toHttpParams(params) }));
  }

  post<T>(url: string, body?: unknown): Observable<T> {
    return this.handle<T>(this.http.post<unknown>(url, body ?? {}));
  }

  put<T>(url: string, body?: unknown): Observable<T> {
    return this.handle<T>(this.http.put<unknown>(url, body ?? {}));
  }

  delete<T>(url: string): Observable<T> {
    return this.handle<T>(this.http.delete<unknown>(url));
  }

  private handle<T>(source: Observable<unknown>): Observable<T> {
    return source.pipe(
      map((raw) => {
        const envelopeError = readApiEnvelopeError(raw);
        if (envelopeError) {
          throw new ApiError(envelopeError, 200);
        }
        return unwrapApiData<T>(raw) as T;
      }),
      catchError((err: unknown) => {
        if (err instanceof ApiError) {
          return throwError(() => err);
        }
        const http = err instanceof HttpErrorResponse ? err : null;
        return throwError(() => new ApiError(this.messageFor(http), http?.status ?? 0, err));
      }),
    );
  }

  /**
   * What the operator reads for a failure, by status first: no response (network, CORS) and 403 have localized
   * messages (the 403 text from the server is hard-coded English); otherwise the envelope `errors`, else a generic one.
   */
  private messageFor(http: HttpErrorResponse | null): string {
    if (http?.status === 0) return this.translation.instant('admin.common.networkError');
    if (http?.status === 403) return this.translation.instant('admin.common.forbidden');
    return (http && readHttpApiErrorMessage(http)) || this.translation.instant('admin.common.requestFailed');
  }
}
